import React, { useCallback, useState, useRef } from "react";

import { Box, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DiaryType, NavigationActionEnum } from "Components/DiaryCalendar";
import { Input } from "Components/Form";
import { addDays, addMonths, addWeeks, endOfWeek, startOfWeek } from "date-fns";
import {
  Calendar2 as IconCalendar,
  ArrowLeft2 as IconArrowLeft,
  ArrowRight2 as IconArrowRight,
  Warning2 as WarningIcon,
} from "iconsax-react";
import { NavigateAction } from "react-big-calendar";

import { ViewTitle } from "./styled";
import { useAppState } from "Context/StateContext";
import { getUserDateFormatPattern } from "Util/dateFormaterPreference";
import { useOperationDespatchContext } from "Modules/moving/contexts/DespatchContext";
import {
  ConfirmChangeModalComponent,
  ConfirmChangeModalHandle,
} from "../../popups/ConfirmChangeModal";

export const DiaryHeaderDateSection = () => {
  const { t: translate } = useTranslation();
  const { currentUser, formatWithUserTimezone } = useAppState();
  const {
    currentDate: actualDate,
    setCurrentDate: setActualDate,
    viewMode,
    changesMade,
    reverseResources,

    persistCrews,
    isLoadingPersistCrewsChanges,
  } = useOperationDespatchContext();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const confirmChangeModalRef = useRef<ConfirmChangeModalHandle>(null);

  const retrieveValueByAction = (action: NavigateAction) => {
    if (action === NavigationActionEnum.NEXT) return 1;
    if (action === NavigationActionEnum.PREV) return -1;
    if (action === NavigationActionEnum.TODAY) return 0;
    if (action === NavigationActionEnum.DATE) return 0;

    return 0;
  };

  const getNewDateByActionAndViewMode = (action: NavigateAction) => {
    switch (viewMode) {
      case DiaryType.MONTH:
        return addMonths(actualDate, retrieveValueByAction(action));
      case DiaryType.WEEK:
        return addWeeks(actualDate, retrieveValueByAction(action));
      case DiaryType.DAY:
        return addDays(actualDate, retrieveValueByAction(action));
      case DiaryType.RESOURCES:
        return addDays(actualDate, retrieveValueByAction(action));
      default:
        return actualDate;
    }
  };

  const onChangeDate = (action: NavigationActionEnum) => {
    const newDate = getNewDateByActionAndViewMode(action);

    if (!changesMade) {
      setActualDate(newDate);
    } else {
      setSelectedDate(newDate);
      confirmChangeModalRef.current?.open();
    }
  };

  const renderDateInformationFormattedByMode = () => {
    switch (viewMode) {
      case DiaryType.MONTH:
        return formatWithUserTimezone(actualDate, "LLLL, yyyy");
      case DiaryType.DAY:
        return formatWithUserTimezone(actualDate, "EEEE, LLLL dd");
      case DiaryType.WEEK: {
        const startWeek = startOfWeek(actualDate);
        const endWeek = endOfWeek(actualDate);

        const startFormat = formatWithUserTimezone(startWeek, "LLLL dd");
        const endFormat = formatWithUserTimezone(endWeek, "LLLL dd");

        return `${startFormat} - ${endFormat}`;
      }
      case DiaryType.RESOURCES:
        return formatWithUserTimezone(
          actualDate,
          getUserDateFormatPattern(currentUser?.preferences)
        );

      default:
        return formatWithUserTimezone(
          actualDate,
          getUserDateFormatPattern(currentUser?.preferences)
        );
    }
  };

  const changeDate = useCallback(
    (event) => {
      const inputDate = new Date(`${event.target.value}T00:00:00`);

      if (!changesMade) {
        setActualDate(inputDate);
      } else {
        setSelectedDate(inputDate);
        confirmChangeModalRef.current?.open();
      }
    },
    [actualDate, changesMade]
  );

  return (
    <>
      <div style={{ marginRight: "16px" }}>
        <IconButton
          onClick={() => onChangeDate(NavigationActionEnum.PREV)}
          color="inherit"
          size="small"
        >
          <IconArrowLeft />
        </IconButton>

        <IconButton
          onClick={() => onChangeDate(NavigationActionEnum.NEXT)}
          color="inherit"
          size="small"
        >
          <IconArrowRight />
        </IconButton>
      </div>

      <Box
        display="flex"
        flexDirection="column"
        marginRight="8px"
        alignItems="center"
        justifyContent="center"
      >
        <ViewTitle>{renderDateInformationFormattedByMode()}</ViewTitle>
      </Box>

      {viewMode === DiaryType.RESOURCES && (
        <Input
          type="date"
          showDateInput={false}
          value={actualDate}
          onChange={changeDate}
          dateButtonSlot={(dateButtonProps) => (
            <IconButton color="inherit" {...dateButtonProps}>
              <IconCalendar variant="Outline" size={20} />
            </IconButton>
          )}
        />
      )}

      <ConfirmChangeModalComponent
        ref={confirmChangeModalRef}
        onReverseResources={reverseResources}
        loading={isLoadingPersistCrewsChanges}
        onSubmit={() => {
          persistCrews(() => {
            if (selectedDate) setActualDate(selectedDate);
            confirmChangeModalRef.current?.close();
          });
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            {translate("youHaveUnsavedChanges")} {translate("doYouWantToSaveBeforeLeaving")}?
          </span>
        </div>
      </ConfirmChangeModalComponent>
    </>
  );
};
